<template>
  <DetailCard
    v-if="!isLoading"
    key="satis-varyant-bilgileri"
    title="Satış ve Varyant Bilgileri"
    cardKey="product-variants"
    :showUpdateButton="false"
    :applyVariantsAllCheckbox="false"
    :cardClass="{
      'without-category': !props.isCategorySelected,
      'product-disabled': isProductDisabled,
    }"
    :showApplyToVariants="true"
    @saveChanges="saveChanges"
    :isLoading="isLoading"
    :isSaved="!isTableReadyToSave"
    :saveButtonText="dynamicSaveButtonText"
    :isSaving="isSaving"
    :showSuccessState="showSuccessState"
    :saveButtonWidth="showSuccessState ? 'auto' : '183px'"
    :isProductDisabled="isProductDisabled"
    :showOtherOptions="showOtherOptions"
    @showOtherOptionsClick="handleShowOtherOptionsClick"
  >
    <div class="info-text" v-if="!props.isCategorySelected">
      <Icons name="InfoCircle" color="#7382a0" />
      <p>
        Satış bilgilerini girebilmek için önce <span>kategori</span> seçimi
        yapmalısınız.
      </p>
    </div>
    <div class="currency-section" :class="{ disabled: isProductDisabled }">
      <div class="varyans-area d-flex flex-column">
        <div class="currency-area">
          <div class="currency-title d-flex flex-row">
            <p>Para Birimi</p>
            <TooltipPlugin
              tooltipText="Fiyat, Merkez Bankası günlük döviz kuru üzerinden otomatik hesaplanacaktır."
              :tooltipWidth="250"
            >
              <template #tooltip-btn>
                <Icons
                  name="InfoCircle"
                  :color="isProductDisabled ? '#C7C7C7' : '#7382a0'"
                />
              </template>
            </TooltipPlugin>
          </div>
          <div class="currency-wrapper d-flex flex-row">
            <div class="currency d-flex flex-column">
              <RadioButtonGroup
                v-if="isCurrencyListReady"
                name="changeApproveStatusModal-radioGroup"
                class="changeApproveStatusModal-radioGroup"
                :options="currencyList"
                :disabled="isProductDisabled"
                @select="handleSelectCurrency($event)"
                isCurrency
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="table-area" :class="{ disabled: isProductDisabled }">
      <ProductDetailTableWrapper
        ref="productsTable"
        :selectedCurrency="selectedCurrency"
        :tableData="tableData"
        :attributes="getAttributes"
        :category="getCategory"
        v-model:isReadyToSave="isTableReadyToSave"
        :isSaved="props.isSaved"
        :productId="props.productId"
      ></ProductDetailTableWrapper>
    </div>
    <div class="info-area" :class="{ disabled: isProductDisabled }">
      <Icons
        name="FilledInfoCircle"
        :color="isProductDisabled ? '#B4B4B4' : '#4FA4E5'"
      />
      <p>
        Barkod numarası eksik veya hatalı ise ilgili numaranın yanındaki
        <span>Değiştir / Ekle</span> alanına tıklayarak düzenleme talep
        edebilirsiniz.
      </p>
    </div>
    <template #extraActionButton>
      <AppButton
        v-if="hasVariantAttribute"
        secondary
        md
        @click="openAddVariantModal"
        :buttonStyle="{ width: '148px' }"
        :disabled="isProductDisabled"
        title="Varyant Ekle"
      >
        <template #prependIcon>
          <Icons
            name="Plus"
            :color="isProductDisabled ? '#FFFFFF' : '#5d3ebc'"
            size="sm"
          />
        </template>
      </AppButton>
    </template>
    <template #options>
      <div class="other-table-area" :class="{ disabled: isProductDisabled }">
        <OtherProductOptions
          :categoryId="getCategory"
          :productId="getProductId"
          :modelCode="getModelCode"
          :selectedCurrency="selectedCurrency"
          :isProductDisabled="isProductDisabled"
          :hasVariantAttribute="hasVariantAttribute"
          @openAddVariantModal="openAddVariantModal"
        />
      </div>
    </template>
  </DetailCard>

  <ProductSaleVariantTableSkeleton v-else />

  <ProductDetailVariantModal
    ref="variantModalRef"
    :productId="productId"
    :categoryId="props.category"
    :productDetail="props.productDetail"
    @variant-added="handleVariantAdded"
    @setIsNotificationShow="notification = $event"
  />

  <SuccessResultBox
    v-if="notification"
    @closeSuccessResultBox="notification = false"
    :successProcessProductImg="variantModalRef?.successProcessProductImg"
  />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import SuccessResultBox from '@/views/AddCatalogProduct/partials/SuccessResultBox.vue';
import {
  Icons,
  TooltipPlugin,
  RadioButtonGroup,
  AppButton,
} from '@n11/so-ui-components';
import ProductSaleVariantTableSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductSaleVariantTableSkeleton.vue';
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';
import OtherProductOptions from './OtherProductOptions/OtherProductOptions.vue';
import ProductDetailVariantModal from './Modals/ProductDetailVariantModal.vue';
import ProductDetailTableWrapper from './ProductDetailTableWrapper.vue';
import DetailCard from './DetailCard.vue';

const emit = defineEmits([
  'requestUpdate',
  'saveChanges',
  'hasChanges',
  'saved',
]);
const showSuccessState = ref(false);
const notification = ref(false);
const store = useStore();
const props = defineProps({
  isCategorySelected: {
    type: Boolean,
    required: true,
    default: false,
  },
  category: {
    type: [Number, null],
    required: true,
  },
  modelCode: {
    type: [Number, null],
    required: true,
  },
  productId: {
    type: [Number, null],
    required: true,
  },
  currencies: {
    type: Object,
    required: true,
    default: () => {},
  },
  tableData: {
    type: Array,
    required: false,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  attributes: {
    type: Array,
    required: false,
    default: () => [],
  },
  isSaved: {
    type: Boolean,
    required: true,
    default: false,
  },
  isVariant: {
    type: Boolean,
    required: true,
    default: false,
  },
  showOtherOptions: {
    type: Boolean,
    required: true,
    default: false,
  },
  selectedCurrency: {
    type: String,
    required: true,
    default: null,
  },
  groupId: {
    type: [String, Number, null],
    required: false,
    default: null,
  },
  productDetail: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});

const variantModalRef = ref();
const productsTable = ref(null);
const isTableReadyToSave = ref(false);

const isProductDisabled = computed(
  () => store.getters['products/isProductDisabled'],
);

const getAttributes = computed(() => props.attributes);
const getCategory = computed(() => props.category);

const isSaving = computed(() => {
  return store.getters['products/isProductSaleVariantLoading'];
});
const getModelCode = computed(() => props.modelCode);
const getProductId = computed(() => props.productId);
const hasVariantAttribute = computed(() => {
  return (
    props.productDetail?.attributes?.some((attr) => attr.isVariant) || false
  );
});

const initialCurrencyList = [
  {
    id: 1,
    name: '₺ (TL)',
    currencyCode: 'TRY',
    rate: 1,
  },
];

const selectedCurrency = ref({
  currencyCode: props.selectedCurrency,
  rate: 1,
});

const currencyList = computed(() => {
  const propList = props.currencies?.currencyRates || [];
  const currentSelectedCode = selectedCurrency.value?.currencyCode;

  const formatItem = (item, index, isFromProps = false) => {
    const currencyCode = isFromProps ? item.currency : item.currencyCode;
    const isSelected = currencyCode === currentSelectedCode;

    const baseObj = {
      id: isFromProps ? index + 2 : item.id,
      name: isFromProps
        ? `${item.currency === 'USD' ? '$' : '€'} (${item.currency})`
        : item.name,
      currencyCode,
      rate: item.rate,
    };

    return isSelected ? { ...baseObj, selected: true } : baseObj;
  };

  const basePart = initialCurrencyList.map((item) =>
    formatItem(item, 0, false),
  );
  const propPart = propList.map((item, index) => formatItem(item, index, true));

  return [...basePart, ...propPart];
});

const isCurrencyListReady = computed(() => {
  return currencyList.value.some((item) => item.selected);
});

const dynamicSaveButtonText = computed(() => {
  if (isTableReadyToSave.value) {
    return 'Değişiklikleri Kaydet';
  }
  return props.isSaved ? 'Değişiklikler Kaydedildi' : 'Değişiklikleri Kaydet';
});

watch(
  () => props.selectedCurrency,
  (newVal) => {
    const rates = props.currencies?.currencyRates || [];
    const foundRate = rates.find((c) => c.currencyCode === newVal)?.rate;

    selectedCurrency.value = {
      currencyCode: newVal,
      rate: foundRate || 1,
    };
  },
  { immediate: true },
);

const handleSelectCurrency = (e) => {
  selectedCurrency.value = e;
};

const revertStockAndDurationToInitial = () => {
  productsTable.value?.revertStockAndDurationToInitial?.();
};

const handleShowOtherOptionsClick = () => {
  sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
    button_name: 'Ürünün Diğer Seçeneklerini Gör',
    button_type: 'Other Product Options',
    button_area: 'Order and Variant Information',
    item_id: props.productId,
    tab: 'Ürünün Diğer Varyantları',
  });
};

const openAddVariantModal = async () => {
  if (!variantModalRef.value) {
    return;
  }

  sendProductDetailEvent(GA4_EVENT.BUTTON_CLICK, {
    button_name: 'Varyant Ekle',
    button_type: 'Add Variant',
    button_area: 'Order and Variant Information',
    item_id: String(props.productId || ''),
  });

  // localStorage'a ürün bilgilerini kaydet
  const filteredAttributes =
    props.productDetail?.attributes?.filter(
      (attr) => attr.isMandatory && !attr.isVariant,
    ) || [];

  const variantTemplateData = {
    title: props.productDetail?.groupTitle || '',
    categoryId: props.productDetail?.categoryId || null,
    categoryName: props.productDetail?.categoryName || '',
    brandName:
      props.productDetail?.brandName || props.productDetail?.brand || '',
    modelCode: props.productDetail?.productMainId || '',
    // isMandatory: true ve isVariant: false olan tüm özellikleri filtrele (hem isSlicer: false hem de isSlicer: true)
    attributes: filteredAttributes.map((attr) => ({
      attributeId: attr.attributeId,
      attributeName: attr.attributeName,
      attributeValue: attr.attributeValue,
      attributeValueId: attr.attributeValueId,
      isMandatory: attr.isMandatory,
      isVariant: attr.isVariant,
      isSlicer: attr.isSlicer,
    })),

    // Ürün açıklaması
    description: props.productDetail?.description || '',
    descriptionEditable: props.productDetail?.descriptionEditable || false,

    // Teslimat şablonu
    shipmentTemplate: props.productDetail?.shipmentTemplate || '',
    shipmentId: props.productDetail?.shipmentId || null,

    // Ek bilgiler
    customTextOptions: props.productDetail?.customTextOptions || [],
    maxPurchaseQuantity: props.productDetail?.maxPurchaseQuantity || null,

    // Ürün güvenliği bilgileri (store'dan çekiliyor)
    productSafetyInfo: store.getters['products/getProductSafetyInfo'] || null,
    nutritionInfoRequired: props.productDetail?.nutritionInfoRequired || false,

    // Timestamp - kopyalama zamanı
    copiedAt: new Date().toISOString(),
  };

  localStorage.setItem(
    'variantTemplateData',
    JSON.stringify(variantTemplateData),
  );

  if (typeof variantModalRef.value.isOpen === 'function') {
    const productData = {
      pimsId: props.productId,
      productGroupId: props.groupId,
      categoryId: props.category,
      categoryName: props.productDetail?.categoryName || '',
      brandName: props.productDetail?.brandName || '',
      color: props.productDetail?.color || '',
      ...props.productDetail,
    };
    await variantModalRef.value.isOpen(productData);
  }
};

const handleVariantAdded = (variantData) => {
  emit('requestUpdate');

  store.commit('common/SET_TOASTER_DATA', {
    type: 'success',
    title: 'Başarılı',
    description: 'Varyant başarıyla eklendi. Sayfa yenileniyor...',
  });
};

const getSalesVariant = async () => {
  const rows = productsTable.value?.getVariantsToSave
    ? productsTable.value.getVariantsToSave()
    : [];

  if (!Array.isArray(rows)) {
    return { data: null, isValid: false, message: 'Varyant verisi alınamadı.' };
  }

  if (rows.length === 0) {
    return {
      data: null,
      isValid: false,
      message: 'En az bir varyant olmalıdır.',
    };
  }

  const isValid = rows.every(
    (row) =>
      row.validations && Object.values(row.validations).every((v) => v.valid),
  );

  return {
    data: {
      tableRows: rows,
      currency: selectedCurrency.value,
    },
    isValid,
    message: isValid
      ? 'Varyantlar başarıyla doğrulandı.'
      : 'Doğrulama hatası mevcut.',
  };
};

const formatTableRowsForPayload = (tableRows) => {
  if (!tableRows || tableRows.length === 0) {
    return { products: [] };
  }

  const productsPayload = tableRows
    .map((row) => {
      const salePrice = row.salePrice?._value ?? row.salePrice;
      const listPrice = row.listPrice?._value ?? row.listPrice;
      const vatRate = row.vatRate?._value ?? row.vatRate;
      const stock = row.stock?._value ?? row.stock;
      const duration = row.duration?._value ?? row.duration;
      const stockCode = row.stockCode?._value ?? row.stockCode;
      const originalStockCode =
        row.originalStockCode?._value ?? row.originalStockCode;
      const productId = row.id?._value ?? row.id;
      const preparingTime = duration;
      const sellerStockCode = originalStockCode || stockCode;
      const hasStockCodeChanged =
        originalStockCode &&
        stockCode &&
        String(stockCode) !== String(originalStockCode);

      return {
        sellerStockCode: sellerStockCode ? String(sellerStockCode) : undefined,
        ...(hasStockCodeChanged
          ? { newSellerStockCode: String(stockCode) }
          : {}),
        productId: productId ? Number(productId) : undefined,
        salePrice: salePrice ? Number(salePrice) : undefined,
        listPrice: listPrice ? Number(listPrice) : undefined,
        vatRate: vatRate ? Number(vatRate) : undefined,
        stock: stock ? Number(stock) : undefined,
        preparingTime: preparingTime ? Number(preparingTime) : 1,
        currencyType:
          selectedCurrency?.value?.currencyCode === 'TRY'
            ? 'TL'
            : selectedCurrency?.value?.currencyCode,
      };
    })
    .filter((product) => product.productId && product.sellerStockCode);

  return {
    products: productsPayload,
  };
};

const saveChanges = async () => {
  if (showSuccessState.value) showSuccessState.value = false;

  const result = await getSalesVariant();
  if (result.isValid && result.data) {
    const payloadData = formatTableRowsForPayload(
      Array.isArray(result.data.tableRows) ? result.data.tableRows : [],
    );
    if (payloadData.products.length > 0) {
      emit('saveChanges', payloadData);
    }
  }
};

watch(
  () => props.isSaved,
  (newVal, oldVal) => {
    if (!oldVal && newVal) {
      productsTable.value?.resetDirtyState?.();
      showSuccessState.value = true;
      sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
        button_name: 'Değişiklikleri Kaydet',
        button_type: 'Save Changes',
        button_area: 'Order and Variant Information',
        item_id: props.productId,
      });
      emit('saved');
    }
    if (oldVal && !newVal && showSuccessState.value) {
      showSuccessState.value = false;
    }
  },
);

watch(isTableReadyToSave, (newValue) => {
  if (newValue && showSuccessState.value) {
    showSuccessState.value = false;
  }
  if (newValue) {
    emit('hasChanges');
  }
});

defineExpose({
  getSalesVariant,
  formatTableRowsForPayload,
  resetDirtyState: () => {
    productsTable.value?.resetDirtyState?.();
    isTableReadyToSave.value = false;
    showSuccessState.value = false;
  },
  showSaveAllSuccessState: () => {
    productsTable.value?.resetDirtyState?.();
    isTableReadyToSave.value = false;
    showSuccessState.value = true;
  },
  revertStockAndDurationToInitial,
  setStockCodeErrors: (errors) => {
    productsTable.value?.setStockCodeErrors?.(errors);
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.sales-variants {
  width: 100%;
  display: flex;
  gap: 16px;
  flex-direction: column;
}

:deep(.without-category) {
  background: $colorD05 !important;
}

:deep(.left-actions) {
  button[disabled],
  button:disabled {
    svg,
    path {
      fill: #ffffff !important;
      stroke: #ffffff !important;
      color: #ffffff !important;
    }
  }
}

.table-area {
  max-width: 984px;

  &.disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
}

.other-table-area {
  max-width: 1034px;

  &.disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
}

.info-text {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $colorD50;
  font-size: 13px;
  line-height: 18px;

  span {
    font-weight: 600;
  }
}

.currency-section {
  margin-bottom: 24px;

  &.disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
}

.varyans-area {
  gap: 24px;

  .currency-area {
    display: flex;
    flex-direction: row;
    gap: 24px;
  }

  .currency-title {
    align-items: flex-start;
    gap: 4px;
    font-size: 14px;
    line-height: 18px;
    color: $colorN80;
    font-weight: 600;
  }

  .currency-wrapper {
    gap: 36px;
    font-size: 12px;
    line-height: 16px;

    .currency {
      align-items: center;
      gap: 8px;

      label {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
      }

      span {
        font-size: 10px;
        line-height: 12px;
        font-weight: 400;
        color: $colorD40;
      }
    }
  }
}

.info-area {
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  background-color: $colorB05;
  padding: 16px;
  border-radius: 6px;
  margin-top: 12px;

  p {
    color: $colorN80;
    font-family: 'Open Sans';
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.05px;

    span {
      font-weight: 700;
    }
  }

  &.disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
}
</style>
